import { useCallback, useMemo, useState } from 'react';
import { find, intersection, isNil, upperCase } from 'lodash';
import cn from 'classnames';
import { observer } from 'mobx-react';
import { GlobalToken, Skeleton, theme } from 'antd';

import { useMeasurement } from '@/hooks/useMeasurement';
import { useStores } from '@/hooks/use-stores';
import { maxLength, onlyDigits, onlyDigitsAndDash } from '@/utils/util-functions';
import FlowIcon from '@/comp/common/icons/FlowIcon';
import PressureIcon from '@/comp/common/icons/PressureIcon';
import ControlDisconnectedIcon from '@/comp/common/icons/ControlDisconnectedIcon';
import ControlConnectedIcon from '@/comp/common/icons/ControlConnectedIcon';
import EPTable from '@/comp/EPTable';
import TableHeaderCell from '@/comp/pages/EnginePage/Engine/Common/TableHeaderCell/TableHeaderCell';
import { Input } from '@/comp/pages/EnginePage/Engine/Common/styled';
import ValidationTooltip from '@/comp/pages/EnginePage/Engine/Common/ValidationTooltip/ValidationTooltip';
import SensorWidgetWithIcon from '@/comp/common/icons/SensorWidgetWithIcon';
import { t } from 'i18next';
import { RangeContainer, TooltipUl } from '../styled';
import { columnsMapping, LONG_DASH, negativeColumns, rulesKeys, ValidMetricTypes } from '../../EngineVersionUtils';
import MetricTooltipContent from './MetricTooltipContent';
import { formatRenderedValue, getOnSetpointFieldChange, getRulesColumns, RangeProps, RangeType } from './Range.utils';

const rangeTypeMapping = (token: GlobalToken) => ({
  [RangeType.Flow]: {
    label: RangeType.Flow,
    widget: <SensorWidgetWithIcon Icon={<FlowIcon />} className="flow-icon" backgroundColor={token.colorBgContainer} />,
    unit: 'm3/min',
  },
  [RangeType.Pressure]: {
    label: RangeType.Pressure,
    widget: (
      <SensorWidgetWithIcon
        Icon={<PressureIcon />}
        className="pressure-icon"
        backgroundColor={token.colorBgContainer}
      />
    ),
    unit: 'bar',
  },
});

const columnsTitleTooltips = {
  order: (
    <TooltipUl>
      <li>{t('set0ForACompressor').replace('{}', t('trimmer'))}</li>
      <li>{t('setNegativeValueForCompressorsNotToBeIncluded').replace('{}', t('not'))}</li>
      <li>{t('setPositiveValueForCompressors').replace('{}', t('base'))}</li>
    </TooltipUl>
  ),
};

const { useToken } = theme;

const RangeComponent = observer(({ range, unit, type, setpoints, fromFallbackOverview, flowIsFirst }: RangeProps) => {
  const { tunit } = useMeasurement();
  const { engineStore, devicesStore } = useStores();
  const { token } = useToken();

  const [, setIsDropdownOpen] = useState(false);

  const validationMessages = useMemo(() => engineStore.validationMessages, [engineStore.validationMessages]);

  const engineMetricSetpoints = useMemo(() => engineStore.engineMetricSetpoints, [engineStore.engineMetricSetpoints]);

  const metricsByCompressor = useMemo(() => {
    const result = {};
    if (engineMetricSetpoints) {
      Object.values(engineMetricSetpoints).forEach((metric: { metricType: string; deviceId: string }) => {
        if (Object.values(ValidMetricTypes).includes(metric.metricType as ValidMetricTypes)) {
          if (!result[metric.deviceId]) {
            result[metric.deviceId] = [];
          }
          result[metric.deviceId].push(metric);
        }
      });
    }
    return result;
  }, [engineMetricSetpoints]);

  const compressorTooltipContents = useMemo(() => {
    const tooltips = {};
    Object.keys(metricsByCompressor).forEach(compressorId => {
      const metrics = metricsByCompressor[compressorId];
      tooltips[compressorId] =
        metrics && metrics.length > 0 ? <MetricTooltipContent metrics={metrics} /> : t('noMetricsAvailable');
    });
    return tooltips;
  }, [metricsByCompressor]);

  const isLoadingStep = useMemo(() => engineStore.isLoadingStep, [engineStore.isLoadingStep]);

  const getEditableFieldsStatusForCompressor = useCallback(
    (field: string | number, value: number, compressorId: string, setpointKey: string) => {
      const compressor = find(devicesStore.compressors, { id: compressorId });
      const editableFields = compressor?.editableFields;
      const editableFlag = !!intersection(editableFields, columnsMapping[field])?.length;
      const oldValue = engineStore.setpointsConfiguration?.getOldSetpointByKey(setpointKey)?.[field];
      const hasBeenEdited = isNil(oldValue) ? !isNil(value) : oldValue !== value;

      return {
        editableFlag,
        hasBeenEdited,
      };
    },
    [devicesStore.compressors, setpoints],
  );

  const rules = rulesKeys.map(rule => {
    const onRuleKeyPress = negativeColumns.some(column => column === rule.key)
      ? (event: unknown) => onlyDigitsAndDash(event)
      : (event: unknown) => onlyDigits(event);
    return {
      dataIndex: rule.key,
      key: rule.key,
      title: <TableHeaderCell titleKey={rule.titleKey} tooltipContent={columnsTitleTooltips[rule.key]} />,
      render: (renderedValue, { key, compressorId }) => {
        const { editableFlag, hasBeenEdited } = getEditableFieldsStatusForCompressor(
          rule.key,
          renderedValue,
          compressorId,
          key,
        );

        const inputElement = (
          <Input
            value={renderedValue}
            onChange={onSetpointFieldChange(key, rule.key)}
            onClick={onInputClick}
            onWheel={onInputScroll}
            placeholder={LONG_DASH}
            bordered={false}
            count={maxLength(4)}
            onKeyPress={onRuleKeyPress}
            $hasBeenEdited={hasBeenEdited}
            $valueLength={`${renderedValue ?? ''}`?.length}
            className={cn(hasBeenEdited && 'edited')}
          />
        );

        return (
          <div className="table-cell">
            {isLoadingStep ? (
              <Skeleton.Button active size="small" />
            ) : !fromFallbackOverview && !engineStore.isReadOnly && editableFlag ? (
              rule.key === 'order' ? (
                <div>
                  {inputElement}
                   {find(validationMessages, { key, field: 'order' })?.message && (
                     <div className="validation-error-indicator" />
                   )}
                </div>
              ) : (
                <ValidationTooltip
                  message={find(validationMessages, { key, field: rule.key })?.message}
                  tArgs={find(validationMessages, { key, field: rule.key })?.tArgs}
                  placement="top"
                  getPopupContainer={() => document.getElementById('flow-level')}
                >
                  {inputElement}
                </ValidationTooltip>
              )
            ) : (
              formatRenderedValue(renderedValue)
            )}
          </div>
        );
      },
    };
  });

  const onSetpointFieldChange = getOnSetpointFieldChange(engineStore);

  const onInputScroll = e => e?.target?.blur();

  const onInputClick = e => e.stopPropagation();

  const rulesColumns = getRulesColumns(
    rules,
    getEditableFieldsStatusForCompressor,
    compressorTooltipContents,
    formatRenderedValue,
    onSetpointFieldChange,
    onInputClick,
    engineStore,
    fromFallbackOverview,
    setIsDropdownOpen,
  );

  return (
    <RangeContainer type={type} $fromFallbackOverview={fromFallbackOverview}>
      {(fromFallbackOverview ? type === RangeType.Flow : type === RangeType.Pressure ? flowIsFirst : true) && (
        <div className="range-container">
          <span className="range-label">{range?.name?.toUpperCase()}</span>
          <div className={type === RangeType.Pressure ? cn('flex-row', 'pressure-range-values') : 'flex-column'}>
            {rangeTypeMapping(token)[type].widget}
            <div className="range-values">
              <span className="range-bounds">
                {range?.lowerBound}-{range?.upperBound}
              </span>
              <span className="range-unit">{tunit(rangeTypeMapping(token)[type].unit, upperCase(unit))}</span>
            </div>
          </div>
        </div>
      )}
      {type === RangeType.Pressure && (
        <div className="rule-container">
          <div className="table-container">
            <div className="rule-name">
              <span className="rule-label">{setpoints?.[0]?.name ?? range?.name ?? <span>&nbsp;</span>}</span>
              {fromFallbackOverview &&
                (setpoints?.[0]?.parentRangeKey ? <ControlConnectedIcon /> : <ControlDisconnectedIcon />)}
            </div>
            <EPTable columns={rulesColumns} dataSource={setpoints} pagination={false} className="table" />
          </div>
        </div>
      )}
    </RangeContainer>
  );
});

export default RangeComponent;
